import React from 'react';
import { useDispatch } from 'react-redux';

import { AuthorizerContext } from 'app/providers/acl/AclProvider';
import { showConfirmCancelDialog, Utils } from 'app/main/components';
import { useMountEffect, usePagination, useSelection, useSettings } from 'app/main/components/hooks';
import { DetailsAdditionalPropsContext, ViewTable } from 'app/main/components/page-layouts';
import { type ViewFooterCustomInfoProps } from 'app/main/components/page-layouts/view/components/ViewFooter';

import ContractsLinesTableFooterInfo from '../../lines/ContractsLinesTableFooterInfo';
import useContractsLinesTabColumns from '../../lines/useContractsLinesTableColumns';

interface ContractsLinesTabProps {
	contract: any;
	updateRefresh: VoidFunction;
}

export default function ContractsLinesTab({ contract, updateRefresh }: ContractsLinesTabProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const authorizer = React.useContext(AuthorizerContext);

	const paginationProps = usePagination({
		module: 'contracts_lines',
		filter: {
			defaultFilters: [
				{ filt_field: 'cntr_deleted', filt_clause: 'not_equal', filt_value: 1 },
				{ filt_field: 'cnli_cntr_uid', filt_clause: 'equals', filt_value: contract.cntr_uid }
			]
		}
	});

	const canUpdate = authorizer.can('update', 'contracts');

	const { changeAdditionalProp } = React.useContext<any>(DetailsAdditionalPropsContext);

	const { selectedItems: selectedLines, setSelectedItems: setSelectedLines } = useSelection({
		module: 'contracts_lines',
		isSelecting: canUpdate,
		editCallback: newSelectedLines => changeAdditionalProp?.('selectedContractLines', newSelectedLines || [])
	});

	const columns = useContractsLinesTabColumns({ updateRefresh });

	function showDeleteLinesDialog(lines: any[]) {
		dispatch(
			showConfirmCancelDialog({
				title: lines?.length > 1 ? t('DIALOG_TITLE_DELETE_MULTIPLE') : t('DIALOG_TITLE_DELETE_GENERIC'),
				onConfirm: () => {
					dispatch(Utils.alertProgressMsg(t));
					Utils.executeStored({
						action: 'st_contracts_lines_delete',
						dispatch,
						data: {
							cntr_uid: contract.cntr_uid,
							ids: lines.map(cnli => cnli.cnli_uid)
						},
						successHandler: () => {
							dispatch(Utils.alertSuccessMsg(t));
							setSelectedLines([]);
							updateRefresh();
						}
					});
				}
			})
		);
	}

	useMountEffect(() => {
		changeAdditionalProp('setSelectedLines', setSelectedLines);
		changeAdditionalProp('showDeleteLinesDialog', showDeleteLinesDialog);
	});

	const FooterCustomInfo = React.useCallback(({ additionalInfo }: ViewFooterCustomInfoProps) => {
		return <ContractsLinesTableFooterInfo totAmount={additionalInfo.tot_amount} />;
	}, []);

	return (
		<ViewTable
			module="contracts_lines"
			columns={columns}
			paginationProps={paginationProps}
			slots={{ footerCustomInfo: FooterCustomInfo }}
			selectionProps={canUpdate ? { mode: 'multiple', selectedItems: selectedLines } : undefined}
		/>
	);
}
